/**
 * @DEPRECATED. Only used for email at the moment (since conversations layer). Use views/messaging-inbox/sidebar-right/useAppData.
 */

// @ts-ignore
import { getUserData } from "@/auth/getUserData";
import useActiveChat from "@/composables/useActiveChat";
import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import { AppInterface } from "@/interfaces/app.interface";
import {
    AgentAppDataInterface,
    AppDataInterface,
    ChannelAppDataInterface,
    ConversationAppDataInterface,
    MessageAppDataInterface,
    TemplateAppDataInterface,
} from "@/interfaces/appData.interface";
import ActiveChatInterface from "@/interfaces/inbox/activeChat.interface";
import axios from "@/libs/axios";
import { AppDataRequired } from "@/types/appDataRequired.type";
import { computed, DeepReadonly, readonly, Ref, ref, watch } from "@vue/composition-api";
import { useApps } from "@/composables/useApps";

function getCurrentChat(activeChat: DeepReadonly<Ref<ActiveChatInterface | undefined>>) {
    const chatLog = activeChat.value?.chatLog;
    if (!chatLog || chatLog?.length === 0) {
        return;
    }

    return chatLog[chatLog.length - 1];
}

function getUserInfo(userId: number): Promise<AgentAppDataInterface> {
    return axios
        .get<AgentAppDataInterface>(`/users/${userId}?fields=[id,name,displayName,email,role]`)
        .then((result) => {
            // TODO: dont fetch settings when not asking for them
            // @ts-ignore
            delete result.data.settings;
            return result.data;
        });
}

function getChannels(withAuth: boolean): Promise<ChannelAppDataInterface[]> {
    const { inboxModuleType } = useInboxModuleType();

    /*if (inboxModuleType.value === InboxModuleType.EMAIL) {
        queryParams.params.s.platform = {
            $eq: InboxModuleType.EMAIL, // EQUAL email
        };
    } else {
        queryParams.params.s.platform = {
            $ne: InboxModuleType.EMAIL, // NOT email
        };
    }*/

    return axios
        .get<ChannelAppDataInterface[]>(`/channels`, {
            params: {
                fields: `[id,name,provider,platform,identity,active,createdAt,customerCareWindowLength${
                    withAuth ? ",auth" : ""
                }]`,
            },
        })
        .then((result) =>
            result.data.map((channel) => {
                // TODO: dont fetch settings when not asking for them
                // @ts-ignore
                delete channel.settings;
                return channel;
            })
        );
}

function getTemplates(): Promise<TemplateAppDataInterface[]> {
    return axios
        .get(`/templates`, {
            params: {
                join: ["channels"],
            },
        })
        .then((response) => response.data);
}

async function getAssignedAgent(
    activeChat: DeepReadonly<Ref<ActiveChatInterface | undefined>>
): Promise<AgentAppDataInterface | undefined> {
    const chat = getCurrentChat(activeChat);

    if (chat === undefined || chat.assignedUserId == null) {
        return;
    }

    return getUserInfo(chat.assignedUserId);
}

async function getContact(activeChat: DeepReadonly<Ref<ActiveChatInterface | undefined>>, withMetadata = false) {
    if (activeChat.value === undefined) {
        return {};
    }

    return axios
        .get(`/contacts/${activeChat.value.contactId}`, {
            params: withMetadata
                ? {
                      join: ["metadata||id,name,value"],
                  }
                : {},
        })
        .then((response) => response.data);
}

function getConversation(
    activeChat: DeepReadonly<Ref<ActiveChatInterface | undefined>>
): ConversationAppDataInterface | undefined {
    const chat = getCurrentChat(activeChat);

    if (chat === undefined) {
        return;
    }

    return {
        start: chat.start,
        status: chat.status,
    };
}

function getMessages(activeChat: DeepReadonly<Ref<ActiveChatInterface | undefined>>): MessageAppDataInterface[] {
    const chat = getCurrentChat(activeChat);

    if (chat === undefined) {
        return [];
    }

    // Only pass on message events
    return chat.entries
        .filter((entry) => entry.type === "text")
        .map((entry) => ({
            direction: entry.direction,
            time: entry.time,
            status: entry.status,
            message: entry.message,
        }));
}

function getTags(activeChat: DeepReadonly<Ref<ActiveChatInterface | undefined>>): string[] {
    return activeChat.value?.tags.map((tag) => tag.name) ?? [];
}

const state: Ref<AppDataInterface> = ref<AppDataInterface>({});
const activeAppId: Ref<number | null> = ref<number | null>(null);

const isLoading: Ref<boolean> = ref<boolean>(false);

const appModalIsActive: Ref<boolean> = ref<boolean>(false);
const appModalContent: Ref<unknown> = ref<unknown>(null);

export default function useAppData() {
    const { getActiveChat } = useActiveChat();
    const { apps }: { apps: Ref<AppInterface[]> } = useApps();

    const setActiveApp = (appId: number | null) => (activeAppId.value = appId);

    const activeApp = computed(() => apps.value.find((app) => app.id === activeAppId.value));

    watch([activeAppId, getActiveChat], async (value, oldValue) => {
        isLoading.value = true;
        // No active app, os no appData
        if (activeAppId.value == null) {
            state.value = {};

            isLoading.value = false;
            return;
        }

        const dataRequired = activeApp.value?.package?.saysimple?.dataRequired;

        // If there is no dataRequired set, appData should be an empty object
        if (dataRequired == null || dataRequired.length === 0) {
            state.value = {};
            isLoading.value = false;
            return;
        }

        const appData: AppDataInterface = {};

        let agentRequest;
        let assignedAgentRequest;
        let contactRequest;
        let channelsRequest;
        let templatesRequest;

        if (dataRequired.indexOf(AppDataRequired.AGENT) >= 0) {
            agentRequest = getUserInfo(getUserData().id);
        }

        if (dataRequired.indexOf(AppDataRequired.ASSIGNED_AGENT) >= 0) {
            assignedAgentRequest = getAssignedAgent(getActiveChat);
        }

        if (dataRequired.indexOf(AppDataRequired.CONTACT) >= 0) {
            const withMetadata = dataRequired.indexOf(AppDataRequired.CONTACT_METADATA) >= 0;
            contactRequest = getContact(getActiveChat, withMetadata);
        }

        if (dataRequired.indexOf(AppDataRequired.CHANNELS) >= 0) {
            const withChannelsAuth = dataRequired.indexOf(AppDataRequired.CHANNELS_AUTH) >= 0;
            channelsRequest = getChannels(withChannelsAuth);
        }

        if (dataRequired.indexOf(AppDataRequired.TEMPLATES) >= 0) {
            templatesRequest = getTemplates();
        }

        if (dataRequired.indexOf(AppDataRequired.CONVERSATION) >= 0) {
            appData[AppDataRequired.CONVERSATION] = getConversation(getActiveChat);
        }

        if (dataRequired.indexOf(AppDataRequired.MESSAGES) >= 0) {
            appData[AppDataRequired.MESSAGES] = getMessages(getActiveChat);
        }

        if (dataRequired.indexOf(AppDataRequired.TAGS) >= 0) {
            appData[AppDataRequired.TAGS] = getTags(getActiveChat);
        }

        const [agent, assignedAgent, contact, channels, templates] = await Promise.all([
            agentRequest,
            assignedAgentRequest,
            contactRequest,
            channelsRequest,
            templatesRequest,
        ]);

        appData[AppDataRequired.AGENT] = agent;
        appData[AppDataRequired.ASSIGNED_AGENT] = assignedAgent;
        appData[AppDataRequired.CONTACT] = contact;
        appData[AppDataRequired.CHANNELS] = channels;
        appData[AppDataRequired.TEMPLATES] = templates;

        state.value = appData;
        isLoading.value = false;
    });

    const setAppModalActive = (appModalContentData: any) => {
        appModalContent.value = appModalContentData;
        appModalIsActive.value = true;
    };

    return {
        appData: readonly(state),
        setActiveApp,
        isLoading,

        // App Modal
        activeApp,
        appModalContent,
        setAppModalActive,
        appModalIsActive,
    };
}
